import { useState } from 'react';
import type React from 'react';

/**
 * ENTSCHEIDUNGEN VON CLAUDE (`AskUserQuestion`) — AUSWAEHLEN, DANN ABSENDEN.
 *
 * Rafael, 20.09.2026: *"wenn ich entscheidungen beantworte dann schickt er die
 * sofort raus wenn ich irgendwo draufklicke statt dass er wartet bis ich unten
 * auf alle senden klicke"*. Vorher rief jeder Options-Klick direkt `onRespond`
 * auf (`CuiLitePanel.tsx:229` und die wortgleiche Kopie `ChatMessages.tsx:107`).
 * Bei MEHREREN Fragen beendete damit schon die erste Antwort die Wartezeit: die
 * Sitzung lief weiter, der Block war nicht mehr die letzte Nachricht und wurde
 * nicht mehr interaktiv gerendert — die uebrigen Fragen konnten gar nicht mehr
 * beantwortet werden.
 *
 * DIESELBE MECHANIK WIE `EntscheidungenListe` in `GuidePanel.tsx` (dort seit
 * 12.09. richtig, siehe deren Kommentar): ein Klick waehlt nur LOKAL, nochmal
 * klicken waehlt ab, und erst der Absenden-Knopf unten schickt alle gesetzten
 * Antworten in EINER Nachricht.
 *
 * SOFORT-AUSNAHME, wie von Rafael festgelegt (*"ausser es ist nur eine frage -
 * dann wird direkt abgeschickt"*): bei GENAU EINER Frage OHNE Mehrfachauswahl
 * sendet der Klick unveraendert sofort. Mit `multiSelect` gilt die Ausnahme
 * NICHT — nach dem ersten Klick waere dort keine zweite Wahl mehr moeglich,
 * und genau das ist der gemeldete Fehler.
 *
 * MEHRFACHAUSWAHL: `multiSelect` stand bisher nur im Typ und wurde nirgends
 * ausgewertet — eine Mehrfachauswahl konnte nie mehr als ein Kuerzel
 * zurueckgeben.
 *
 * EINE KOMPONENTE FUER BEIDE PANELS. Die zwei Kopien dieses Renderers waren
 * genau die Drift, vor der der Kommentar bei `handleRespond` (CuiLitePanel)
 * warnt. `aiIdPrefix` haelt die bestehenden `data-ai-id`s stabil, damit die
 * Bedien-Automatisierung nichts verliert.
 */

export interface AskUserQuestionItem {
  question: string;
  header?: string;
  options?: Array<{ label: string; description?: string }>;
  multiSelect?: boolean;
}

const RAHMEN: React.CSSProperties = {
  margin: '8px 0', padding: '10px 14px', background: 'rgba(59,130,246,0.08)',
  borderRadius: 6, border: '1px solid rgba(59,130,246,0.2)',
};

export function AskUserQuestionBlock({ questions, onRespond, aiIdPrefix, rawInput }: {
  questions: AskUserQuestionItem[];
  onRespond?: (text: string) => void;
  aiIdPrefix: string;
  /* Roher `block.input` — nur fuer den Fallback unten, damit eine Frage ohne
     auswertbare `questions` nicht als leere Flaeche endet, sondern zeigt, was
     tatsaechlich ankam. */
  rawInput?: unknown;
}) {
  /* Auswahl pro Frage. Mehrfachauswahl = mehrere Kuerzel, sonst genau eines. */
  const [gewaehlt, setGewaehlt] = useState<Record<number, string[]>>({});
  const [gesendet, setGesendet] = useState(false);

  /*
   * FALLBACK OHNE FRAGEN — unveraendert aus beiden Panels uebernommen: kommt
   * `questions` leer an, ist der Rohinhalt des Blocks immer noch mehr wert als
   * eine leere Flaeche, und ein Freitextfeld haelt die Sitzung bedienbar.
   */
  if (questions.length === 0) {
    const rawText = rawInput ? JSON.stringify(rawInput, null, 2) : 'Frage ohne Inhalt';
    return (
      <div style={RAHMEN}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tn-info)', marginBottom: 8 }}>Frage von Claude</div>
        <pre style={{ fontSize: 11, color: 'var(--tn-text)', whiteSpace: 'pre-wrap', marginBottom: 8, background: 'var(--tn-bg)', padding: 8, borderRadius: 4 }}>{rawText}</pre>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            data-ai-id={`${aiIdPrefix}-question-freetext`}
            type="text"
            placeholder="Antwort eingeben..."
            style={{ flex: 1, padding: '6px 10px', fontSize: 12, background: 'var(--tn-bg)', border: '1px solid var(--tn-border)', borderRadius: 4, color: 'var(--tn-text)' }}
            onKeyDown={(e) => { if (e.key === 'Enter') { onRespond?.((e.target as HTMLInputElement).value); } }}
          />
        </div>
      </div>
    );
  }

  const sofort = questions.length === 1 && !questions[0].multiSelect;
  const anzahlBeantwortet = questions.filter((_, qi) => (gewaehlt[qi]?.length ?? 0) > 0).length;

  /* NOCHMAL KLICKEN = ABWAEHLEN — derselbe Klick zurueck, kein zweites
     Bedienelement (ein Knopf im Knopf ist ungueltiges HTML). */
  const umschalten = (qi: number, label: string, mehrfach: boolean) => {
    setGewaehlt(g => {
      const bisher = g[qi] ?? [];
      if (bisher.includes(label)) {
        const rest = bisher.filter(l => l !== label);
        const naechst = { ...g };
        if (rest.length) naechst[qi] = rest; else delete naechst[qi];
        return naechst;
      }
      return { ...g, [qi]: mehrfach ? [...bisher, label] : [label] };
    });
  };

  /*
   * EINE NACHRICHT FUER ALLE ANTWORTEN. Pro beantworteter Frage eine Zeile
   * "<Ueberschrift>: <Kuerzel>", damit die Zuordnung eindeutig bleibt, wenn
   * mehrere Fragen offen waren. Uebersprungene Fragen gehen nicht mit — wie
   * bei `EntscheidungenListe.absenden`.
   */
  const absenden = () => {
    if (gesendet) return;
    const zeilen = questions
      .map((q, qi) => ({ q, labels: gewaehlt[qi] ?? [] }))
      .filter(x => x.labels.length > 0)
      .map(({ q, labels }) => `${q.header || q.question}: ${labels.join(', ')}`);
    if (!zeilen.length) return;
    setGesendet(true);
    onRespond?.(zeilen.join('\n'));
  };

  /* DOPPELKLICK-SCHUTZ. Fehlte hier bisher als einziger Interaktionszweig —
     `ExitPlanMode` hat ihn seit jeher, hier gingen zwei Klicks als zwei
     Nachrichten raus. */
  const sofortSenden = (label: string) => {
    if (gesendet) return;
    setGesendet(true);
    onRespond?.(label);
  };

  return (
    <div style={RAHMEN} data-ai-id={`${aiIdPrefix}-question-block`}>
      {questions.map((q, qi) => {
        const mehrfach = !!q.multiSelect;
        const gewaehltHier = gewaehlt[qi] ?? [];
        return (
          <div key={qi} style={{ marginBottom: qi < questions.length - 1 ? 12 : 0 }}>
            {q.header && (
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--tn-info)', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
                {q.header}{mehrfach ? ' · Mehrfachauswahl' : ''}
              </span>
            )}
            <p style={{ fontSize: 13, color: 'var(--tn-text)', marginBottom: 8, fontWeight: 500 }}>{q.question}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {q.options?.map((opt, oi) => {
                const aktiv = gewaehltHier.includes(opt.label);
                return (
                  <button
                    key={opt.label}
                    type="button"
                    data-ai-id={`${aiIdPrefix}-question-option-${qi}-${oi}`}
                    disabled={gesendet}
                    onClick={() => (sofort ? sofortSenden(opt.label) : umschalten(qi, opt.label, mehrfach))}
                    style={{
                      padding: '6px 14px', borderRadius: 4,
                      cursor: gesendet ? 'default' : 'pointer',
                      /* BLAU STATT NEUTRAL: eine nur LOKAL gewaehlte, noch nicht
                         gesendete Option muss sich von einer abgeschickten
                         Antwort unterscheiden lassen. */
                      background: aktiv ? 'var(--tn-blue)33' : 'var(--tn-bg)',
                      border: `1px solid ${aktiv ? 'var(--tn-blue)' : 'var(--tn-border)'}`,
                      color: 'var(--tn-text)', fontSize: 12, textAlign: 'left' as const,
                      opacity: gesendet ? 0.6 : 1,
                    }}
                    onMouseEnter={e => { if (!aktiv && !gesendet) e.currentTarget.style.background = 'var(--tn-bg-highlight)'; }}
                    onMouseLeave={e => { if (!aktiv && !gesendet) e.currentTarget.style.background = 'var(--tn-bg)'; }}
                    title={opt.description}
                  >
                    <strong>{opt.label}</strong>
                    {opt.description && <span style={{ display: 'block', fontSize: 11, color: 'var(--tn-text-muted)', marginTop: 2 }}>{opt.description}</span>}
                    {aktiv && !gesendet && (
                      <span style={{ display: 'block', fontSize: 10, color: 'var(--tn-blue)', marginTop: 3 }}>
                        gewählt, noch nicht gesendet (nochmal klicken: abwählen)
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      {gesendet ? (
        <div data-ai-id={`${aiIdPrefix}-question-sent`} style={{ fontSize: 12, color: 'var(--tn-ok)', fontWeight: 600, padding: '8px 0 2px' }}>
          Antwort gesendet — wird verarbeitet...
        </div>
      ) : !sofort && (
        <button
          type="button"
          data-ai-id={`${aiIdPrefix}-question-absenden`}
          disabled={anzahlBeantwortet === 0}
          onClick={absenden}
          style={{
            display: 'block', width: '100%', textAlign: 'center', padding: '9px 12px',
            borderRadius: 6, fontSize: 12, fontWeight: 600, marginTop: 10,
            cursor: anzahlBeantwortet === 0 ? 'default' : 'pointer',
            background: anzahlBeantwortet === 0 ? 'var(--tn-bg)' : 'var(--tn-info)',
            color: anzahlBeantwortet === 0 ? 'var(--tn-text-muted)' : '#fff',
            border: '1px solid var(--tn-info)',
          }}
        >
          {anzahlBeantwortet === 0
            ? 'Absenden (erst eine Antwort wählen)'
            : `Absenden (${anzahlBeantwortet} von ${questions.length})`}
        </button>
      )}
    </div>
  );
}
